import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Lock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/form-field';
import { apiResetPassword } from '@/features/users/api';
import { extractErrorMessage } from '@/lib/api-client';

const schema = z.object({
  newPassword: z.string().min(10, 'Mínimo 10 caracteres'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  if (!token) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>Link inválido ou expirado. Solicite um novo.</AlertDescription>
        </Alert>
        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
          Solicitar nova redefinição
        </Link>
      </div>
    );
  }

  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-success/10 text-success">
          <CheckCircle className="size-7" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold">Senha redefinida!</h2>
          <p className="text-sm text-muted-foreground">Sua nova senha já está ativa.</p>
        </div>
        <Button asChild className="w-full" size="lg">
          <Link to="/login">Fazer login</Link>
        </Button>
      </div>
    );
  }

  const onSubmit = async (values: z.infer<typeof schema>) => {
    setError(null);
    try {
      await apiResetPassword(token, values.newPassword);
      setSuccess(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h2 className="text-3xl font-semibold tracking-tight">Nova senha</h2>
        <p className="text-sm text-muted-foreground">Escolha uma senha forte para sua conta.</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <FormField control={form.control} name="newPassword" label="Nova senha" type="password" placeholder="Mínimo 10 caracteres" autoFocus />
        <FormField control={form.control} name="confirmPassword" label="Confirmar senha" type="password" placeholder="Repita a nova senha" />
        <Button type="submit" size="lg" className="w-full" loading={form.formState.isSubmitting}>
          <Lock className="size-4" /> Redefinir senha
        </Button>
      </form>
    </div>
  );
}
